/**
 * Sticky narrative — crossfade images on scroll.
 *
 * @package HelloElementorChild
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  initCamstImpegnoAccent,
  teardownCamstImpegnoAccent,
  refreshCamstImpegnoAccent,
} from './camst-impegno-accent.js';

gsap.registerPlugin(ScrollTrigger);

var stickyInstances = new WeakMap();
var STICKY_DESKTOP_MIN = 1024;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isStickyNarrativeStacked() {
  return window.matchMedia('(max-width: ' + (STICKY_DESKTOP_MIN - 1) + 'px)').matches;
}

function isStickyNarrativeMobileMode() {
  return isStickyNarrativeStacked() && !prefersReducedMotion();
}

function setActiveImage(root, index) {
  root.querySelectorAll('[data-camst-sticky-narrative-image]').forEach(function (image) {
    var imageIndex = parseInt(image.getAttribute('data-camst-sticky-narrative-image'), 10);
    image.classList.toggle('is-active', imageIndex === index);
  });
}

function setActiveBlock(root, index) {
  root.querySelectorAll('[data-camst-sticky-narrative-block]').forEach(function (block, blockIndex) {
    block.classList.toggle('is-active', blockIndex === index);
  });

  var blocksWrap = root.querySelector('.camst-sticky-narrative__blocks');

  if (blocksWrap) {
    blocksWrap.scrollTop = 0;
  }
}

function getTrackMetrics(track) {
  var rect = track.getBoundingClientRect();
  var scrollTop = window.scrollY || window.pageYOffset;
  var trackTop = scrollTop + rect.top;
  var trackHeight = track.offsetHeight;
  var viewportHeight = window.innerHeight;
  var scrollRange = Math.max(1, trackHeight - viewportHeight);

  return {
    trackTop: trackTop,
    scrollRange: scrollRange,
  };
}

function getScrollProgress(root) {
  var track = root.querySelector('[data-camst-sticky-narrative-track]');

  if (!track) {
    return 0;
  }

  var metrics = getTrackMetrics(track);
  var scrollY = window.scrollY || window.pageYOffset;
  var raw = (scrollY - metrics.trackTop) / metrics.scrollRange;

  return Math.max(0, Math.min(1, raw));
}

function getBlockIndexFromProgress(progress, count) {
  if (count <= 1) {
    return 0;
  }

  var segment = 1 / count;

  return Math.max(0, Math.min(count - 1, Math.floor((progress + segment * 0.1) / segment)));
}

function killStickyTriggers(instance) {
  if (instance && instance.triggers) {
    instance.triggers.forEach(function (trigger) {
      trigger.kill();
    });
    instance.triggers.length = 0;
  }
}

function unbindStickyScroll(root) {
  if (root._camstStickyNarrativeOnScroll) {
    window.removeEventListener('scroll', root._camstStickyNarrativeOnScroll);
    window.removeEventListener('resize', root._camstStickyNarrativeOnScroll);
    root._camstStickyNarrativeOnScroll = null;
  }
}

function bindDesktopTriggers(root, blocks, fade, triggers) {
  blocks.forEach(function (block, index) {
    triggers.push(
      ScrollTrigger.create({
        trigger: block,
        start: 'top 50%',
        end: 'bottom 50%',
        onToggle: function (self) {
          if (self.isActive) {
            setActiveImage(root, index);
          }
        },
      })
    );
  });

  if (fade) {
    var fadeTrigger = root.querySelector('[data-camst-sticky-narrative-track]') || root;

    triggers.push(
      ScrollTrigger.create({
        trigger: fadeTrigger,
        start: 'bottom 90%',
        end: 'bottom 20%',
        scrub: 1,
        onUpdate: function (self) {
          fade.style.opacity = String(self.progress);
        },
      })
    );
  }
}

function bindMobileScroll(root) {
  var count = parseInt(root.getAttribute('data-count') || '0', 10);

  if (!count) {
    return;
  }

  var ticking = false;

  function updateFromScroll() {
    var progress = getScrollProgress(root);
    var nextIndex = getBlockIndexFromProgress(progress, count);

    if (nextIndex !== root._camstStickyActiveIndex) {
      root._camstStickyActiveIndex = nextIndex;
      setActiveImage(root, nextIndex);
      setActiveBlock(root, nextIndex);
    }

    var fade = root.querySelector('[data-camst-sticky-narrative-fade]');

    if (fade) {
      var fadeProgress = Math.max(0, Math.min(1, (progress - 0.8) / 0.2));
      fade.style.opacity = String(fadeProgress);
    }
  }

  function onScrollOrResize() {
    if (ticking) {
      return;
    }

    ticking = true;

    requestAnimationFrame(function () {
      ticking = false;
      updateFromScroll();
    });
  }

  root._camstStickyActiveIndex = 0;
  root._camstStickyNarrativeOnScroll = onScrollOrResize;
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  onScrollOrResize();
}

function setupStickyNarrative(root) {
  var instance = stickyInstances.get(root) || { triggers: [] };
  var blocks = Array.prototype.slice.call(
    root.querySelectorAll('[data-camst-sticky-narrative-block]')
  );
  var fade = root.querySelector('[data-camst-sticky-narrative-fade]');

  killStickyTriggers(instance);
  unbindStickyScroll(root);

  root.querySelectorAll('[data-camst-sticky-narrative-block]').forEach(function (block) {
    block.classList.remove('is-active');
  });

  if (blocks[0]) {
    blocks[0].classList.add('is-active');
  }

  setActiveImage(root, 0);

  if (fade) {
    fade.style.opacity = '0';
  }

  if (isStickyNarrativeMobileMode()) {
    bindMobileScroll(root);
  } else {
    root._camstStickyActiveIndex = 0;
    bindDesktopTriggers(root, blocks, fade, instance.triggers);
    ScrollTrigger.refresh();
  }

  stickyInstances.set(root, instance);
}

export function initCamstStickyNarrative(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-sticky-narrative]'));
  } else if (scope.matches && scope.matches('[data-camst-sticky-narrative]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-sticky-narrative]'));
  }

  roots.forEach(function (root) {
    if (stickyInstances.has(root)) {
      return;
    }

    setupStickyNarrative(root);
    initCamstImpegnoAccent(root);

    var onResize = function () {
      setupStickyNarrative(root);
    };

    root._camstStickyNarrativeOnResize = onResize;
    window.addEventListener('resize', onResize, { passive: true });
  });
}

export function teardownCamstStickyNarrative(scope) {
  var roots = scope
    ? scope.matches && scope.matches('[data-camst-sticky-narrative]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-sticky-narrative]'))
    : [];

  roots.forEach(function (root) {
    var instance = stickyInstances.get(root);

    if (root._camstStickyNarrativeOnResize) {
      window.removeEventListener('resize', root._camstStickyNarrativeOnResize);
      root._camstStickyNarrativeOnResize = null;
    }

    killStickyTriggers(instance);
    unbindStickyScroll(root);

    var fade = root.querySelector('[data-camst-sticky-narrative-fade]');

    if (fade) {
      fade.style.opacity = '0';
    }

    root.querySelectorAll('[data-camst-sticky-narrative-block]').forEach(function (block, index) {
      block.classList.toggle('is-active', index === 0);
    });

    setActiveImage(root, 0);
    teardownCamstImpegnoAccent(root);
    stickyInstances.delete(root);
    delete root._camstStickyActiveIndex;
  });
}

export function refreshCamstStickyNarrative() {
  document.querySelectorAll('[data-camst-sticky-narrative]').forEach(function (root) {
    if (stickyInstances.has(root)) {
      setupStickyNarrative(root);
    }
  });

  ScrollTrigger.refresh();
  refreshCamstImpegnoAccent();
}
